import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createUseStyles, Loader, Modal, Text } from '@Sage/hoi-poi-ui';

import { useIframeEmbedBridge } from '../hooks/useIframeEmbedBridge';
import {
    CLOSE_REASONS,
    DEFAULT_CHILD_CANCEL_TIMEOUT_MS,
    DEFAULT_IFRAME_MIN_HEIGHT_PX,
    DEFAULT_IFRAME_SANDBOX,
    HOST_CHROME_CANCEL_SOURCE,
} from '../protocol/embedProtocol';
import EmbedFooter from './EmbedFooter';
import EmbedIframe from './EmbedIframe';
import styles from './ExternalEmbedModal.styles';

const useStyles = createUseStyles(styles, 'ExternalEmbedModal');

const ExternalEmbedModal = memo(
    ({
        allow,
        allowedOrigin,
        childCancelTimeoutMs = DEFAULT_CHILD_CANCEL_TIMEOUT_MS,
        chromeCloseCancelText = 'Cancel',
        chromeCloseConfirmActionText = 'Discard changes',
        chromeCloseConfirmText,
        chromeCloseConfirmTitle = 'Discard unsaved changes?',
        confirmOnChromeClose = false,
        footerComponent,
        getAuthContext,
        handshakeTimeoutMs = 0,
        iframeSrc,
        iframeSizing = 'auto',
        iframeTitle = 'External embed',
        initContext,
        initExtras,
        isHostPreparing = false,
        isOpen,
        loadErrorText = 'Unable to load embedded content.',
        maxIframeHeight,
        mode,
        onClose,
        onEmbedReady,
        onInitError,
        onEmbedNotify,
        renderStateFooter = true,
        sandbox = DEFAULT_IFRAME_SANDBOX,
        size = 'big',
        title,
    }) => {
        const classes = useStyles();

        // --- Refs / state
        const iframeRef = useRef(null);
        const waitingForChildCloseRef = useRef(false);
        const closeHandledRef = useRef(false);
        const [isIframeLoading, setIsIframeLoading] = useState(true);
        const [loadError, setLoadError] = useState(false);
        const [isChromeConfirmOpen, setIsChromeConfirmOpen] = useState(false);

        // --- Derived
        const isIframeFillSizing = iframeSizing === 'fill';
        const hasHostLoadError = !isHostPreparing && (loadError || !iframeSrc);

        // --- Handlers
        const finalizeClose = useCallback(
            (payload = {}) => {
                if (closeHandledRef.current) return;

                closeHandledRef.current = true;
                waitingForChildCloseRef.current = false;
                setIsChromeConfirmOpen(false);
                onClose?.(payload);
            },
            [onClose],
        );

        const showHostLoadError = useCallback(() => {
            setLoadError(true);
            setIsIframeLoading(false);
        }, []);

        const handleInitError = useCallback(
            (error) => {
                showHostLoadError();
                onInitError?.(error);
            },
            [onInitError, showHostLoadError],
        );

        const {
            iframePixelHeight,
            iframeState,
            isHandshakeReady,
            requestChildCancel,
            resetBridge,
            sendActionToIframe,
            sendInit,
        } = useIframeEmbedBridge({
            allowedOrigin,
            childCancelTimeoutMs,
            getAuthContext,
            handshakeTimeoutMs,
            iframeRef,
            iframeSrc,
            initContext,
            initExtras,
            isActive: isOpen && !isHostPreparing,
            maxIframeHeight,
            mode,
            onCloseFromEmbed: finalizeClose,
            onEmbedReady,
            onHandshakeTimeout: showHostLoadError,
            onInitError: handleInitError,
            onNotify: onEmbedNotify,
        });

        const startChildCancelClose = useCallback(() => {
            if (waitingForChildCloseRef.current) return;

            waitingForChildCloseRef.current = true;
            requestChildCancel({ source: HOST_CHROME_CANCEL_SOURCE });
        }, [requestChildCancel]);

        const handleIframeLoad = useCallback(() => {
            setIsIframeLoading(false);
            setLoadError(false);
            sendInit?.({ reason: 'iframeLoad' })?.catch(() => {
                // Best-effort host context. Partners ignore init; do not hide a loaded URL.
            });
        }, [sendInit]);

        const handleIframeError = useCallback(() => {
            setIsIframeLoading(false);
            setLoadError(true);
        }, []);

        const handleRequestClose = useCallback(() => {
            if (waitingForChildCloseRef.current) return;

            if (hasHostLoadError || !isHandshakeReady) {
                finalizeClose({ reason: CLOSE_REASONS.CANCEL });
                return;
            }

            if (confirmOnChromeClose) {
                setIsChromeConfirmOpen(true);
                return;
            }

            startChildCancelClose();
        }, [
            confirmOnChromeClose,
            finalizeClose,
            hasHostLoadError,
            isHandshakeReady,
            startChildCancelClose,
        ]);

        const handleChromeConfirm = useCallback(() => {
            setIsChromeConfirmOpen(false);
            startChildCancelClose();
        }, [startChildCancelClose]);

        const handleChromeConfirmCancel = useCallback(() => {
            setIsChromeConfirmOpen(false);
        }, []);

        const handleAfterClose = useCallback(() => {
            waitingForChildCloseRef.current = false;
            closeHandledRef.current = false;
            setIsIframeLoading(true);
            setLoadError(false);
            setIsChromeConfirmOpen(false);
            resetBridge();
        }, [resetBridge]);

        // --- Effects
        useEffect(() => {
            if (isOpen) {
                closeHandledRef.current = false;
                return;
            }

            waitingForChildCloseRef.current = false;
            setIsChromeConfirmOpen(false);
        }, [isOpen]);

        // --- Render props
        const iframeStyle = useMemo(() => {
            if (isIframeFillSizing) {
                return {
                    height: '100%',
                };
            }

            const height = iframePixelHeight ?? DEFAULT_IFRAME_MIN_HEIGHT_PX;

            return {
                height,
            };
        }, [iframePixelHeight, isIframeFillSizing]);

        const resolvedFooterComponent = useMemo(() => {
            if (footerComponent) {
                return typeof footerComponent === 'function'
                    ? footerComponent({
                          iframeState,
                          isIframeLoading,
                          loadError: hasHostLoadError,
                          onRequestClose: handleRequestClose,
                          sendActionToIframe,
                      })
                    : footerComponent;
            }

            if (hasHostLoadError) return null;

            if (!renderStateFooter) return null;

            if (iframeState?.customFooter) {
                return (
                    <EmbedFooter footer={iframeState.customFooter} onAction={sendActionToIframe} />
                );
            }

            return <EmbedFooter footer={iframeState?.footer} onAction={sendActionToIframe} />;
        }, [
            footerComponent,
            handleRequestClose,
            hasHostLoadError,
            iframeState,
            isIframeLoading,
            renderStateFooter,
            sendActionToIframe,
        ]);

        const modalProps = useMemo(
            () => ({
                footerComponent: resolvedFooterComponent,
                height: Number.isFinite(maxIframeHeight) ? maxIframeHeight : undefined,
                isOpen,
                onAfterClose: handleAfterClose,
                onRequestClose: handleRequestClose,
                shouldCloseOnEsc: true,
                shouldCloseOnOverlayClick: true,
                size,
                title,
                useCornerClose: true,
                overrides: {
                    content: {
                        style: {
                            flex: 1,
                            ...(isIframeFillSizing
                                ? {
                                      display: 'flex',
                                      minHeight: 0,
                                      overflow: 'hidden',
                                  }
                                : {}),
                        },
                    },
                },
            }),
            [
                handleAfterClose,
                handleRequestClose,
                isIframeFillSizing,
                isOpen,
                maxIframeHeight,
                resolvedFooterComponent,
                size,
                title,
            ],
        );

        const iframeProps = useMemo(
            () => ({
                ref: iframeRef,
                allow,
                className: isIframeFillSizing
                    ? `${classes.iframe} ${classes.iframeFill}`
                    : classes.iframe,
                onError: handleIframeError,
                onLoad: handleIframeLoad,
                sandbox: sandbox,
                src: iframeSrc,
                style: {
                    ...iframeStyle,
                    position: isIframeLoading ? 'absolute' : 'relative',
                    visibility: isIframeLoading ? 'hidden' : 'visible',
                },
                title: iframeTitle,
            }),
            [
                allow,
                classes.iframe,
                classes.iframeFill,
                handleIframeError,
                handleIframeLoad,
                iframeSrc,
                iframeStyle,
                iframeTitle,
                isIframeFillSizing,
                isIframeLoading,
                sandbox,
            ],
        );

        const rootClassName = isIframeFillSizing
            ? `${classes.root} ${classes.rootFill}`
            : classes.root;

        return (
            <>
                <Modal {...modalProps}>
                    <div className={rootClassName} data-handshake-ready={String(isHandshakeReady)}>
                        {hasHostLoadError && (
                            <Text className={classes.error} type="body">
                                {loadErrorText}
                            </Text>
                        )}
                        {(isHostPreparing || (!hasHostLoadError && isIframeLoading)) && (
                            <div className={classes.loader} style={iframeStyle}>
                                <Loader size="huge" />
                            </div>
                        )}
                        {!hasHostLoadError && !isHostPreparing && iframeSrc && (
                            <EmbedIframe {...iframeProps} />
                        )}
                    </div>
                </Modal>
                {confirmOnChromeClose && (
                    <Modal
                        cancelText={chromeCloseCancelText}
                        confirmText={chromeCloseConfirmActionText}
                        isOpen={isChromeConfirmOpen}
                        onCancel={handleChromeConfirmCancel}
                        onConfirm={handleChromeConfirm}
                        onRequestClose={handleChromeConfirmCancel}
                        shouldCloseOnEsc
                        shouldCloseOnOverlayClick
                        size="small"
                        title={chromeCloseConfirmTitle}
                        useCornerClose={false}
                    >
                        {chromeCloseConfirmText && (
                            <Text type="body">{chromeCloseConfirmText}</Text>
                        )}
                    </Modal>
                )}
            </>
        );
    },
);

ExternalEmbedModal.displayName = 'ExternalEmbedModal';

export default ExternalEmbedModal;
